import { useAsync, useAsyncFn } from 'react-use';
import { useContract } from '@thirdweb-dev/react-core';
import { BigNumber, ethers } from 'ethers';
import { ThirdwebSDK, useAddress, useSigner } from '@thirdweb-dev/react';
import { parseUnits } from '@app/helpers/format';

import VaultAbi from '../../../abis/steer/VaultErc20.json';
import { useMemo } from 'react';
import { useVaultDetailsByAddress } from '@app/hooks/plugins/useVaultDetailsByAddress';
import { Vault } from '@app/hooks/plugins/useVaults';
import { Pool } from '@app/types/pool';
import { QueryKeys } from '@app/constants/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { useSupportedChain } from '@app/hooks/other/useSupportedChain';
import { trimDecimalsOverflow } from '@app/helpers/decimals';

export function useWithdrawLiquidity(
  data: {
    totalAmounts: [BigNumber, BigNumber] | undefined;
    totalSupply: BigNumber | undefined;
    balance: BigNumber | undefined;
    token0: BigNumber | undefined;
    token1: BigNumber | undefined;
    vault: Vault;
  },
  amount: string,
  poolInfo: Pool
) {
  const address = useAddress();

  const queryClient = useQueryClient();

  const chain = useSupportedChain();

  const signer = useSigner();

  const { contract } = useContract(data.vault.lpToken.id, VaultAbi.abi);

  const { value: balance } = useAsync(async () => {
    if (!contract) {
      return;
    }

    const bal = await contract.call('balanceOf', [address]);

    return ethers.utils.formatUnits(bal, data.vault.lpToken.decimals);
  }, [address, contract, data]);

  const [{ loading: withdrawingLiquidity, error }, withdraw] =
    useAsyncFn(async () => {
      if (!contract || !signer) {
        return;
      }

      const sdk = ThirdwebSDK.fromSigner(signer);

      const _c = await sdk.getContract(data.vault.lpToken.id, VaultAbi.abi);

      try {
        // @ts-ignore
        await _c.call('withdraw', [
          parseUnits(amount, +data.vault.lpToken.decimals),
          BigNumber.from(0),
          BigNumber.from(0),
          address
        ]);

        await queryClient.invalidateQueries([QueryKeys.STEER_VAULTS_POSITIONS]);
      } catch (e) {
        console.error(e);
      }
    }, [contract, address, amount, queryClient, chain, signer]);

  const { data: vaultDetails } = useVaultDetailsByAddress(
    data.vault.lpToken.id
  );

  const output = useMemo(() => {
    if (!amount || !vaultDetails || !data.totalSupply || !data.totalAmounts) {
      return;
    }

    const vaultToken0Balance = data.totalAmounts[0]!;

    const vaultToken1Balance = data.totalAmounts[1]!;

    const totalLPTokensIssued = data.totalSupply;

    const lpTokenValue = ethers.utils.parseUnits(
      trimDecimalsOverflow(amount, +data.vault.lpToken.decimals),
      +data.vault.lpToken.decimals
    );

    const token0Val = lpTokenValue
      .mul(vaultToken0Balance)
      .div(totalLPTokensIssued);

    const token1Val = lpTokenValue
      .mul(vaultToken1Balance)
      .div(totalLPTokensIssued);

    return {
      amount0: ethers.utils.formatUnits(token0Val, +poolInfo.token0.decimals),
      amount1: ethers.utils.formatUnits(token1Val, +poolInfo.token1.decimals)
    };
  }, [
    amount,
    vaultDetails,
    data.totalSupply,
    data.totalAmounts,
    data.vault.lpToken.decimals,
    poolInfo.token0.decimals,
    poolInfo.token1.decimals
  ]);

  return {
    withdraw,
    withdrawingLiquidity,
    error,
    balance: balance ?? '0',
    output
  };
}
